import React, { useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { Layout } from "Components/Layout/Layout";
import { Footer } from "Components/Footer/Footer";
import { CourseCard } from "Components/CourseCard/CourseCard";
import { Tabs } from "Components/Tabs/Tabs";
import { Pagination } from "Components/Pagination/Pagination";
import { useMobile } from "Hooks/useMobile";
import { useCursosPaquetes } from "Hooks/useCursosPaquetes";
import { useFavorite } from "Hooks/useFavorite";
import { InformativeText } from "Components/InformativeText/InformativeText";
import { NotebookIcon } from "Components/Icons/Documents/NotebookIcon";
import { CourseCardSkeleton } from "Components/CourseCardSkeleton/CourseCardSkeleton";
import { RUTAS } from "rutas";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import { useQuery } from "Hooks/useQuery";
import { paramService } from "Services/param_service";
import {
  TIPOS_APRENDIZAJE,
  DIPLOMA_FIRMA_URL,
  HTTP_METHODS,
  HTTP_HEADERS,
} from "Constantes/constants";
import "./MisPaquetesDeCursos.scss";

const TABS = [{ label: "Asignados" }, { label: "Terminados" }];

export const MisPaquetesDeCursos = () => {
  const { authUser } = useSelector((state) => state.auth);
  const isMobile = useMobile();
  const { paquetes: allPaquetes, isLoading: isLoadingAllPaquetes } =
    useCursosPaquetes();
  const { toggleFavorite, isItemProcessing } = useFavorite(authUser.idUsuario);
  const [activeTab, setActiveTab] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingDiplomas, setDownloadingDiplomas] = useState(new Set());
  const itemsPerPage = isMobile ? 10 : 9;
  const isAssignedTab = activeTab === 1;
  const navigate = useNavigate();

  const { data: apiDiploma } = useQuery(paramService.param, {
    grupo: "apis",
  });

  const getParamValue = (data, codigo) => {
    if (!data || !Array.isArray(data)) return null;
    const param = data.find((p) => p.codigoParametro === codigo);
    return param?.parametro || null;
  };

  const endpointDiploma = getParamValue(apiDiploma?.data, "apiDiploma") || null;

  const isLoading = isLoadingAllPaquetes;

  const paquetes = useMemo(() => {
    if (!allPaquetes) return [];

    return allPaquetes.filter((paquete) => paquete.asignado);
  }, [allPaquetes]);

  const paquetesData = useMemo(() => {
    if (isAssignedTab) {
      return paquetes.filter(
        (paquete) => paquete.progreso.porcentajeTotal < 100
      );
    } else {
      return paquetes.filter(
        (paquete) => paquete.progreso.porcentajeTotal === 100
      );
    }
  }, [paquetes, isAssignedTab]);

  const { paginatedData, totalPages } = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const slicedData = paquetesData.slice(startIndex, endIndex);
    const total = Math.ceil(paquetesData.length / itemsPerPage);

    return {
      paginatedData: slicedData,
      totalPages: total,
    };
  }, [paquetesData, currentPage, itemsPerPage]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const handleCardClick = (paquete) => {
    const path = RUTAS.PAQUETE(paquete.idPaquete);
    navigate(path);
  };

  const handleDownloadDiploma = useCallback(
    async (paquete) => {
      if (!endpointDiploma) return;

      const paqueteId = paquete.idPaquete;
      if (downloadingDiplomas.has(paqueteId)) return;

      setDownloadingDiplomas((prev) => new Set(prev).add(paqueteId));

      try {
        const diplomaData = {
          dpi: authUser?.usuario,
          idTipoAprendizaje: TIPOS_APRENDIZAJE.PAQUETE,
          idAprendizaje: paqueteId,
          firma: DIPLOMA_FIRMA_URL,
        };

        const response = await fetch(endpointDiploma, {
          method: HTTP_METHODS.POST,
          headers: {
            "Content-Type": HTTP_HEADERS.CONTENT_TYPE_JSON,
          },
          body: JSON.stringify(diplomaData),
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        saveAs(blob, `DIPLOMA ${paquete.titulo}.pdf`);
      } catch (error) {
        console.error("Error descargando diploma:", error);
      } finally {
        setDownloadingDiplomas((prev) => {
          const newSet = new Set(prev);
          newSet.delete(paqueteId);
          return newSet;
        });
      }
    },
    [endpointDiploma, authUser, downloadingDiplomas]
  );

  return (
    <Layout>
      <div className="mis-paquetes-de-cursos">
        <div className="mis-paquetes-de-cursos__tabs-wrapper">
          <Tabs items={TABS} activeTab={activeTab} onChange={handleTabChange} />
          <h1 className="mis-paquetes-de-cursos__title">
            Mis Paquetes de Cursos:&nbsp;
            {isLoading ? "00" : paquetes.length.toString().padStart(2, "0")}/
            {isLoading
              ? "00"
              : (allPaquetes || []).length.toString().padStart(2, "0")}
          </h1>
        </div>

        <div className="mis-paquetes-de-cursos__container">
          {!isLoading && paquetesData.length === 0 && (
            <div className="mis-paquetes-de-cursos__empty-state">
              <InformativeText>
                <NotebookIcon />
                {isAssignedTab
                  ? "Aún no tienes paquetes de cursos asignados."
                  : "Aún no terminas un paquete de cursos."}
              </InformativeText>
            </div>
          )}

          <div className="mis-paquetes-de-cursos__grid">
            {!isLoading &&
              paginatedData.map((paquete) => {
                const isProcessing = isItemProcessing(paquete.idAlterno);
                const isDownloadingDiploma = downloadingDiplomas.has(
                  paquete.idPaquete
                );

                return (
                  <CourseCard
                    key={paquete.idAlterno}
                    title={paquete.titulo}
                    imageUrl={paquete.img}
                    duration={paquete.duracion}
                    difficulty={paquete.dificultad}
                    language={paquete.idioma}
                    badgeText={paquete.categoria}
                    badgeTextColor={paquete.colorTexto}
                    badgeBackgroundColor={paquete.colorFondo}
                    badgeBorderColor={paquete.colorBorde}
                    isFavorite={Boolean(paquete.favorito)}
                    variant={isAssignedTab ? "progress" : "completed"}
                    isLoggedIn={true}
                    onToggleFavorite={() => toggleFavorite(paquete)}
                    onClick={() => handleCardClick(paquete)}
                    onViewAgain={() => handleCardClick(paquete)}
                    isProcessingFavorite={isProcessing}
                    progress={Math.trunc(paquete.progreso.porcentajeTotal)}
                    timeRemaining={Math.trunc(paquete.tiempoPendiente)}
                    onDownloadDiploma={() => handleDownloadDiploma(paquete)}
                    isDownloadingDiploma={isDownloadingDiploma}
                  />
                );
              })}

            {isLoading &&
              Array.from({ length: itemsPerPage }).map((_, index) => (
                <CourseCardSkeleton
                  key={index}
                  variant={isAssignedTab ? "progress" : "completed"}
                />
              ))}
          </div>
        </div>

        {paquetesData.length > 0 && (
          <div className="mis-paquetes-de-cursos__pagination-container">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
            />
          </div>
        )}
      </div>
      <Footer />
    </Layout>
  );
};
